import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { ActionButton, IButtonStyles, IconButton, mergeStyleSets, Stack, StackItem } from '@fluentui/react'
import FileHeader from '../Render/FileHeader'
import { generatePath } from 'react-router'
import { fileRoutePrefix } from '../../hooks/file/useFilePages'
import { isAppContext } from '../../helpers/appHelper'
import { AppContext } from '../../config/appConfig'
import ComposeEmailDialog, { ComposeEmailDialogProps } from '../Dialog/OutlookItem/ComposeEmailDialog'
import ComposeAppointmentDialog, { ComposeAppointmentDialogProps } from '../Dialog/OutlookItem/ComposeAppointmentDialog'
import { FileAction } from '../../config/fileConfig'
import Card from '../Card'
import { ViewFileProps } from '../../pages/File/ViewFile'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { isOfficeInitialized } from '../../helpers/outlookHelper'
import DialogButton from '../Button/DialogButton'
import { DocumentGenerationParams, DocumentGenerationType } from '../../hooks/document/useDocumentGenerationHandler'
import useSubmitDocumentGenerationRequest from '../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { ModuleType } from '../../context/GlobalContext'
import useModules from '../../hooks/module/useModules'
import useRoles, { Role, rolesTasks, rolesTimes } from '../../hooks/useRoles'
import { useTranslation } from 'react-i18next'

type FileCardParamsDto = DocumentGenerationParams<{
    fileId: string
}>

export default function ViewFileHeader({ file, fileId }: ViewFileProps): ReactElement {
    const dispatch = useDispatch()
    const history = useHistory()
    const roles = useRoles()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const { isViewport, viewport } = useViewport()

    const routeMatch = useRouteMatch<{ fileId: string }>()
    const submitRequest = useSubmitDocumentGenerationRequest<FileCardParamsDto>(DocumentGenerationType.FileCard)
    const actionButtonStyles: IButtonStyles = useMemo(
        () =>
            mergeStyleSets({
                root: {
                    height: 'inherit',
                },
                flexContainer: {
                    flexDirection: isViewport('>=', Viewport.XL) ? 'row' : 'column',
                    gap: isViewport('>=', Viewport.XL) ? 0 : '4px',
                },
            }),
        [viewport],
    )

    return (
        <Card>
            <Stack horizontal>
                <Stack grow tokens={{ childrenGap: 0 }}>
                    <Stack tokens={{ padding: 's1' }}>
                        <FileHeader file={file.current.data} showTitle showDescription />
                    </Stack>
                    <StackItem grow>
                        <span />
                    </StackItem>
                    <Stack horizontal>
                        {modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes) && (
                            <ActionButton
                                text={isViewport('>', Viewport.Medium) && translate('action.create.time.title')}
                                iconProps={{ iconName: 'Timer' }}
                                styles={actionButtonStyles}
                                onClick={() =>
                                    history.push(generatePath(`${fileRoutePrefix}/time/add`, routeMatch.params))
                                }
                            />
                        )}
                        {modules.has(ModuleType.Tasks) && roles.any(rolesTasks) && (
                            <ActionButton
                                text={isViewport('>', Viewport.Medium) && translate('action.create.task.title')}
                                iconProps={{ iconName: 'TaskLogo' }}
                                styles={actionButtonStyles}
                                onClick={() =>
                                    history.push(generatePath(`${fileRoutePrefix}/task/add`, routeMatch.params))
                                }
                            />
                        )}
                        {/*Show this button both versions but not when already composing an e-mail.*/}
                        {!isAppContext(AppContext.Compose) && (
                            <DialogButton<ComposeEmailDialogProps>
                                button={ActionButton}
                                buttonProps={{
                                    text: isViewport('>', Viewport.Medium) && translate('action.create.email'),
                                    iconProps: { iconName: 'OutlookLogo' },
                                    styles: actionButtonStyles,
                                }}
                                dialog={ComposeEmailDialog}
                                dialogProps={{ file: file.current.data, parties: file.parties.data }}
                            />
                        )}
                        {isOfficeInitialized() && !isAppContext(AppContext.Compose) && (
                            <DialogButton<ComposeAppointmentDialogProps>
                                button={ActionButton}
                                buttonProps={{
                                    text: isViewport('>', Viewport.Medium) && translate('action.create.appointment'),
                                    iconProps: { iconName: 'Calendar' },
                                    styles: actionButtonStyles,
                                }}
                                dialog={ComposeAppointmentDialog}
                                dialogProps={{ file: file.current.data, parties: file.parties.data }}
                            />
                        )}
                    </Stack>
                </Stack>
                <Stack tokens={{ childrenGap: 0 }}>
                    <IconButton
                        title={translate('file.refresh')}
                        iconProps={{ iconName: 'Refresh' }}
                        onClick={() => dispatch({ type: FileAction.ResetCurrent })}
                    />
                    {roles.none([Role.FileLimited]) && (
                        <IconButton
                            title={translate('file.edit.action')}
                            iconProps={{ iconName: 'Edit' }}
                            onClick={() => history.push(`/file/${fileId}/edit`)}
                        />
                    )}
                    {roles.none([Role.FileLimited]) && (
                        <IconButton
                            title={translate('file.pdf')}
                            iconProps={{ iconName: 'Print' }}
                            onClick={() => {
                                submitRequest({ fileId })
                            }}
                        />
                    )}
                </Stack>
            </Stack>
        </Card>
    )
}
